import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import { CHERRY_DEV } from '../cherryDevBrand';

const P = CHERRY_DEV.cherry;
const L = CHERRY_DEV.leaf;
const D = CHERRY_DEV.cherryDark;

const SvgWrap = ({ children, width = '100%', maxWidth = 420, viewBox = '0 0 400 320', sx = {} }) => (
  <Box
    component="svg"
    viewBox={viewBox}
    xmlns="http://www.w3.org/2000/svg"
    sx={{ width, maxWidth, height: 'auto', display: 'block', ...sx }}
  >
    {children}
  </Box>
);

SvgWrap.propTypes = {
  children: PropTypes.node.isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  viewBox: PropTypes.string,
  sx: PropTypes.object,
};

/** Mini cereza decorativa */
const CherryMark = ({ cx, cy, scale = 1, flip = false }) => (
  <g transform={`translate(${cx}, ${cy}) scale(${flip ? -scale : scale}, ${scale})`}>
    <path d="M0 -10 C-2 -18 -8 -20 -10 -20 C-6 -16 -6 -12 -4 -8" stroke={L} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M0 -10 C2 -18 8 -20 10 -20 C6 -16 6 -12 4 -8" stroke={L} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    <path d="M-4 -16 C-8 -20 -12 -18 -10 -14 C-7 -14 -4 -15 -4 -16Z" fill={L} />
    <circle cx="-6" cy="2" r="8" fill={P} />
    <circle cx="6" cy="2" r="8" fill={D} />
    <circle cx="-8" cy="-1" r="2" fill={CHERRY_DEV.cherryLight} opacity="0.7" />
  </g>
);

CherryMark.propTypes = {
  cx: PropTypes.number.isRequired,
  cy: PropTypes.number.isRequired,
  scale: PropTypes.number,
  flip: PropTypes.bool,
};

export const HeroIllustration = ({ primary = P, secondary = L }) => (
  <SvgWrap maxWidth={480}>
    <defs>
      <linearGradient id="cdHeroBg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={primary} stopOpacity="0.14" />
        <stop offset="100%" stopColor={secondary} stopOpacity="0.1" />
      </linearGradient>
      <linearGradient id="cdHeroBtn" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={primary} />
        <stop offset="100%" stopColor={D} />
      </linearGradient>
      <filter id="cdSoft" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="8" stdDeviation="12" floodColor={primary} floodOpacity="0.12" />
      </filter>
    </defs>
    <rect x="16" y="24" width="368" height="272" rx="28" fill="url(#cdHeroBg)" />
    <rect
      x="44"
      y="52"
      width="312"
      height="216"
      rx="18"
      fill="#FFFBFA"
      stroke={primary}
      strokeOpacity="0.12"
      filter="url(#cdSoft)"
    />
    {/* ventana de código */}
    <rect x="64" y="72" width="180" height="120" rx="12" fill={CHERRY_DEV.ink} />
    <circle cx="78" cy="86" r="4" fill={primary} />
    <circle cx="90" cy="86" r="4" fill={CHERRY_DEV.cherryLight} />
    <circle cx="102" cy="86" r="4" fill={secondary} />
    <text x="74" y="112" fill="#F8A0AB" fontSize="11" fontFamily="ui-monospace, monospace">{'const cherry = {'}</text>
    <text x="84" y="130" fill="#7DDBA8" fontSize="11" fontFamily="ui-monospace, monospace">stack: 'full',</text>
    <text x="84" y="148" fill="#FDE68A" fontSize="11" fontFamily="ui-monospace, monospace">custom: true,</text>
    <text x="84" y="166" fill="#93C5FD" fontSize="11" fontFamily="ui-monospace, monospace">ship: 'fast'</text>
    <text x="74" y="184" fill="#F8A0AB" fontSize="11" fontFamily="ui-monospace, monospace">{'};'}</text>
    {/* panel metrics */}
    <rect x="256" y="72" width="80" height="54" rx="12" fill={primary} fillOpacity="0.1" stroke={primary} strokeOpacity="0.2" />
    <rect x="268" y="86" width="40" height="8" rx="4" fill={primary} fillOpacity="0.35" />
    <rect x="268" y="102" width="56" height="6" rx="3" fill={secondary} fillOpacity="0.35" />
    <rect x="256" y="138" width="80" height="54" rx="12" fill={secondary} fillOpacity="0.1" stroke={secondary} strokeOpacity="0.25" />
    <path d="M268 172 L280 152 L292 162 L308 144" stroke={secondary} strokeWidth="3" fill="none" strokeLinecap="round" />
    <rect x="64" y="208" width="100" height="36" rx="10" fill="url(#cdHeroBtn)" />
    <rect x="176" y="208" width="88" height="36" rx="10" fill={secondary} fillOpacity="0.18" stroke={secondary} strokeOpacity="0.35" />
    <CherryMark cx="320" cy="230" scale={1.1} />
  </SvgWrap>
);

HeroIllustration.propTypes = {
  primary: PropTypes.string,
  secondary: PropTypes.string,
};

export const AccountingIllustration = ({ color = P }) => (
  <SvgWrap maxWidth={168} sx={{ mb: 1 }}>
    <rect x="48" y="48" width="304" height="224" rx="20" fill={color} fillOpacity="0.07" />
    <rect x="80" y="88" width="140" height="100" rx="14" fill="#FFFBFA" stroke={color} strokeOpacity="0.22" />
    <path d="M100 150 L120 128 L140 142 L170 110" stroke={color} strokeWidth="4" fill="none" strokeLinecap="round" />
    <rect x="240" y="88" width="96" height="40" rx="10" fill={color} fillOpacity="0.18" />
    <rect x="240" y="140" width="96" height="48" rx="10" fill={L} fillOpacity="0.14" stroke={L} strokeOpacity="0.3" />
    <text x="268" y="170" fontSize="22" fontWeight="700" fill={color}>$</text>
    <CherryMark cx="300" cy="230" scale={0.85} />
  </SvgWrap>
);

AccountingIllustration.propTypes = { color: PropTypes.string };

export const EcommerceIllustration = ({ color = L }) => (
  <SvgWrap maxWidth={168} sx={{ mb: 1 }}>
    <rect x="48" y="48" width="304" height="224" rx="20" fill={color} fillOpacity="0.07" />
    <rect x="90" y="80" width="220" height="150" rx="14" fill="#FFFBFA" stroke={color} strokeOpacity="0.22" />
    <rect x="110" y="100" width="72" height="72" rx="12" fill={P} fillOpacity="0.12" />
    <CherryMark cx="146" cy="136" scale={0.7} />
    <rect x="200" y="108" width="88" height="10" rx="5" fill={color} fillOpacity="0.28" />
    <rect x="200" y="130" width="64" height="8" rx="4" fill={color} fillOpacity="0.16" />
    <rect x="200" y="156" width="72" height="28" rx="8" fill={P} fillOpacity="0.85" />
  </SvgWrap>
);

EcommerceIllustration.propTypes = { color: PropTypes.string };

export const SchedulingIllustration = ({ color = P }) => (
  <SvgWrap maxWidth={168} sx={{ mb: 1 }}>
    <rect x="56" y="40" width="288" height="240" rx="20" fill={color} fillOpacity="0.07" />
    <rect x="100" y="70" width="200" height="180" rx="14" fill="#FFFBFA" stroke={color} strokeOpacity="0.22" />
    <rect x="120" y="92" width="160" height="28" rx="8" fill={color} fillOpacity="0.16" />
    {[0, 1, 2].map((row) => (
      <g key={row}>
        <rect
          x="120"
          y={140 + row * 32}
          width="36"
          height="20"
          rx="6"
          fill={row === 1 ? color : color}
          fillOpacity={row === 1 ? 0.4 : 0.12}
        />
        <rect x="168" y={144 + row * 32} width="96" height="10" rx="5" fill={color} fillOpacity="0.14" />
      </g>
    ))}
    <CherryMark cx="310" cy="240" scale={0.75} />
  </SvgWrap>
);

SchedulingIllustration.propTypes = { color: PropTypes.string };

export const ApiIllustration = ({ color = L }) => (
  <SvgWrap maxWidth={168} sx={{ mb: 1 }}>
    <rect x="40" y="56" width="320" height="208" rx="20" fill={color} fillOpacity="0.07" />
    <circle cx="120" cy="160" r="42" fill="#FFFBFA" stroke={P} strokeOpacity="0.35" strokeWidth="3" />
    <circle cx="200" cy="118" r="30" fill={P} fillOpacity="0.16" />
    <circle cx="280" cy="176" r="38" fill="#FFFBFA" stroke={color} strokeOpacity="0.35" strokeWidth="3" />
    <path d="M160 148 L182 128" stroke={P} strokeWidth="3" strokeDasharray="6 4" />
    <path d="M228 130 L248 160" stroke={color} strokeWidth="3" strokeDasharray="6 4" />
    <CherryMark cx="120" cy="160" scale={0.55} />
    <rect x="268" y="164" width="24" height="24" rx="6" fill={color} fillOpacity="0.35" />
  </SvgWrap>
);

ApiIllustration.propTypes = { color: PropTypes.string };

export const PainIllustration = ({ color = '#E65100' }) => (
  <SvgWrap maxWidth={280}>
    <rect x="28" y="48" width="344" height="224" rx="24" fill={color} fillOpacity="0.06" />
    <rect x="70" y="90" width="90" height="120" rx="14" fill="#FFFBFA" stroke={color} strokeOpacity="0.2" />
    <rect x="180" y="110" width="90" height="120" rx="14" fill="#FFFBFA" stroke={color} strokeOpacity="0.2" />
    <rect x="290" y="130" width="50" height="100" rx="14" fill="#FFFBFA" stroke={color} strokeOpacity="0.2" />
    <path d="M95 170 L130 140 L155 165" stroke={color} strokeWidth="3" fill="none" opacity="0.5" />
    <path d="M205 150 L240 120 L265 145" stroke={color} strokeWidth="3" fill="none" opacity="0.5" />
    <circle cx="200" cy="78" r="22" fill={P} fillOpacity="0.15" />
    <text x="192" y="86" fontSize="20" fill={P} fontWeight="700">!</text>
  </SvgWrap>
);

PainIllustration.propTypes = { color: PropTypes.string };

export const CaseStudyIllustration = ({ primary = P, secondary = L }) => (
  <SvgWrap maxWidth={220}>
    <rect x="20" y="28" width="360" height="264" rx="22" fill={primary} fillOpacity="0.06" />
    <rect x="50" y="58" width="140" height="100" rx="14" fill="#FFFBFA" stroke={primary} strokeOpacity="0.2" />
    <rect x="210" y="58" width="140" height="100" rx="14" fill="#FFFBFA" stroke={secondary} strokeOpacity="0.25" />
    <rect x="50" y="178" width="300" height="84" rx="14" fill={secondary} fillOpacity="0.1" />
    <path d="M70 128 L100 98 L130 113 L160 83" stroke={primary} strokeWidth="4" fill="none" strokeLinecap="round" />
    <rect x="230" y="88" width="80" height="12" rx="6" fill={secondary} fillOpacity="0.3" />
    <rect x="230" y="112" width="60" height="10" rx="5" fill={secondary} fillOpacity="0.2" />
    <CherryMark cx="320" cy="220" scale={0.9} />
  </SvgWrap>
);

CaseStudyIllustration.propTypes = {
  primary: PropTypes.string,
  secondary: PropTypes.string,
};

export const ProcessIllustration = ({ color = P }) => (
  <SvgWrap maxWidth={200}>
    <circle cx="80" cy="90" r="40" fill={color} fillOpacity="0.12" stroke={color} strokeOpacity="0.28" strokeWidth="2" />
    <circle cx="200" cy="90" r="40" fill={color} fillOpacity="0.2" stroke={color} strokeOpacity="0.32" strokeWidth="2" />
    <circle cx="320" cy="90" r="40" fill={L} fillOpacity="0.2" stroke={L} strokeOpacity="0.35" strokeWidth="2" />
    <path d="M120 90 H160" stroke={color} strokeWidth="3" strokeDasharray="8 6" />
    <path d="M240 90 H280" stroke={color} strokeWidth="3" strokeDasharray="8 6" />
    <text x="72" y="96" fontSize="18" fontWeight="800" fill={color}>1</text>
    <text x="192" y="96" fontSize="18" fontWeight="800" fill={color}>2</text>
    <text x="312" y="96" fontSize="18" fontWeight="800" fill={L}>3</text>
    <rect x="100" y="180" width="200" height="70" rx="16" fill={color} fillOpacity="0.07" />
    <CherryMark cx="200" cy="214" scale={0.85} />
  </SvgWrap>
);

ProcessIllustration.propTypes = { color: PropTypes.string };

export const ConversionIllustration = ({ primary = L, color }) => {
  const c = color || primary;
  return (
    <SvgWrap maxWidth={200}>
      <rect x="40" y="40" width="320" height="240" rx="24" fill={c} fillOpacity="0.07" />
      <rect x="80" y="80" width="120" height="160" rx="14" fill="#FFFBFA" stroke={c} strokeOpacity="0.22" />
      <rect x="220" y="80" width="100" height="160" rx="14" fill={P} fillOpacity="0.1" stroke={P} strokeOpacity="0.2" />
      <rect x="100" y="105" width="80" height="10" rx="5" fill={c} fillOpacity="0.25" />
      <rect x="100" y="130" width="80" height="10" rx="5" fill={c} fillOpacity="0.15" />
      <rect x="100" y="155" width="80" height="28" rx="8" fill={P} fillOpacity="0.85" />
      <circle cx="270" cy="120" r="22" fill="#FFFBFA" stroke={P} strokeOpacity="0.3" />
      <circle cx="270" cy="170" r="22" fill={c} fillOpacity="0.2" />
      <circle cx="270" cy="220" r="22" fill={P} fillOpacity="0.28" />
      <CherryMark cx="270" cy="120" scale={0.45} />
    </SvgWrap>
  );
};

ConversionIllustration.propTypes = {
  primary: PropTypes.string,
  color: PropTypes.string,
};

/** Ilustración nueva: stack / arquitectura */
export const StackIllustration = ({ primary = P, secondary = L }) => (
  <SvgWrap maxWidth={240} viewBox="0 0 400 280">
    <rect x="60" y="40" width="280" height="48" rx="12" fill={primary} fillOpacity="0.9" />
    <text x="80" y="70" fill="#fff" fontSize="14" fontWeight="700" fontFamily="system-ui,sans-serif">Frontend</text>
    <rect x="80" y="104" width="240" height="48" rx="12" fill={secondary} fillOpacity="0.85" />
    <text x="100" y="134" fill="#fff" fontSize="14" fontWeight="700" fontFamily="system-ui,sans-serif">APIs &amp; lógica</text>
    <rect x="100" y="168" width="200" height="48" rx="12" fill={D} fillOpacity="0.9" />
    <text x="120" y="198" fill="#fff" fontSize="14" fontWeight="700" fontFamily="system-ui,sans-serif">Base de datos</text>
    <CherryMark cx="340" cy="220" scale={0.9} />
  </SvgWrap>
);

StackIllustration.propTypes = {
  primary: PropTypes.string,
  secondary: PropTypes.string,
};

export const SOLUTION_ILLUSTRATIONS = {
  contable: AccountingIllustration,
  ecommerce: EcommerceIllustration,
  agendamiento: SchedulingIllustration,
  integracion: ApiIllustration,
};
